import {Button, type ButtonProps} from '@primer/react'
import {useCallback} from 'react'
import {useApproveWorkflows} from '../../../hooks/use-approve-workflows'

export interface ApproveWorkflowsButtonProps {
  /** The head SHA of the pull request, used to identify which workflows to approve. */
  headSha: string
  /** Invoked when an approval attempt starts, e.g. to clear any previously shown error. */
  onApprovalStart?: () => void
  /** Invoked with the error message when approving and re-requesting workflows fails. */
  onError?: (message: string) => void
  size?: ButtonProps['size']
}

/**
 * Button that approves and re-requests workflow runs that are awaiting approval.
 * Uses `useApproveWorkflows` internally to manage the in-flight state and error reporting.
 */
export function ApproveWorkflowsButton({headSha, onApprovalStart, onError, size}: ApproveWorkflowsButtonProps) {
  const {approveWorkflows, isPending} = useApproveWorkflows({
    pullRequestHeadSha: headSha,
    onError,
  })

  const handleClick = useCallback(() => {
    onApprovalStart?.()
    approveWorkflows()
  }, [onApprovalStart, approveWorkflows])

  return (
    <Button loading={isPending} loadingAnnouncement="Re-running workflows" onClick={handleClick} size={size}>
      Approve workflows to run
    </Button>
  )
}
